/**
 * A route for one of the quote worksheet's screens (#6274 split): shown only when useQuoteWorksheetSwitch says it is on
 * for this viewer. `screen="worksheet"` (the default, the worksheet itself): the organization's live switch, or a manager
 * with worksheetForManagers on. `screen="catalog"` (price catalog, pricing, customer quote wording): live, or a manager
 * with either preview switch on (previewForManagers is the price-catalog preview, and never opens the worksheet). Anyone
 * else who opens the address is sent to the Quoting home, as if the page did not exist yet. Visibility only: the
 * callables behind the page enforce who may do what.
 *
 * Inside OrgBoundary, so the gate decides only once the organization is known. On a fresh load the organization context
 * reads "not loading, no organization" for one render after sign-in, exactly like a finished load with none; deciding on
 * that bounced a manager who opened the page by address (#6274 Q2 delta review). OrgBoundary waits through it, and says
 * so with a retry when a load really ends with no organization.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { TableSkeleton } from '../../shared/Skeletons';
import { OrgBoundary } from '../../shared/OrgBoundary';
import { useWorksheetDecision } from '../../../hooks/quoting/useWorksheetDecision';
import { QUOTE_CREATE_PATH, WORKSHEET_PATH, quoteDetailPath } from '../../../lib/quoting/v2/quotePaths';
import type { WorksheetScreen } from '../../../hooks/quoting/useWorksheetDecision';
import { hasQuoteSource } from '../../../lib/quoting/v2/quoteSource';

/**
 * Decides once per navigation, on a fresh read of the switch (useWorksheetDecision): a stale cached live:true from before
 * a rollback is not trusted, and a later re-read (the tab coming back into focus after a rollback) never tears down a
 * worksheet someone is typing in. The server refuses a save the switch no longer allows, and the next visit decides afresh.
 *
 * Off, a new worksheet opened with something to quote from (a ticket, project, lead or generator) goes on to the old Create
 * Quote screen with it rather than the Quoting home (Q9 delta-2): whoever sent it there read the switch earlier than this.
 * A saved draft (?draftId) never does: the old screen cannot open a worksheet quote. It goes to the quote's own page
 * instead (#8450 red-team M2: a link from history, Chat or a bookmark after a rollback), which shows a worksheet quote
 * read-only.
 */
function PreviewGate({ screen, children }: { screen: WorksheetScreen; children: React.ReactNode }) {
  const location = useLocation();
  const worksheet = useWorksheetDecision(screen);
  if (worksheet === null) return <TableSkeleton />;
  if (!worksheet) {
    if (location.pathname === WORKSHEET_PATH) {
      const draftId = new URLSearchParams(location.search).get('draftId');
      if (draftId) return <Navigate replace to={quoteDetailPath(draftId)} />;
      if (hasQuoteSource(location.state)) return <Navigate replace to={QUOTE_CREATE_PATH} state={location.state} />;
    }
    return <Navigate to="/quoting" replace />;
  }
  return <>{children}</>;
}

export function WorksheetPreviewGate({ screen = 'worksheet', children }: { screen?: WorksheetScreen; children: React.ReactNode }) {
  return (
    <OrgBoundary fallback={<TableSkeleton />}>
      <PreviewGate screen={screen}>{children}</PreviewGate>
    </OrgBoundary>
  );
}
